import type { SerializedProject } from '@/types/projects';
import { isProjectWorkspaceReady } from '@/lib/projects/setup';

type EntryProject = Pick<
  SerializedProject,
  'id' | 'setup_status' | 'onboarding_completed' | 'onboarding_job_id' | 'current_user_role'
>;

export function resolveWorkspaceEntry(input: {
  projects: EntryProject[];
  currentProjectId: string | null;
  query: { get(key: string): string | null };
  createdProjectId?: string | null;
}): { mode: 'new' | 'select' | 'setup' | 'home' | 'unavailable'; projectId: string | null } {
  const { projects, currentProjectId, query, createdProjectId } = input;
  const explicitNew = query.get('new_project') === 'true' || Boolean(query.get('reset'));
  if (explicitNew && !createdProjectId) return { mode: 'new', projectId: null };
  const requestedId = createdProjectId || query.get('project_id')?.trim() || null;
  const project = projects.find(item => item.id === (requestedId || currentProjectId));
  if (requestedId && !project) return { mode: 'unavailable', projectId: requestedId };
  if (requestedId && currentProjectId !== requestedId)
    return { mode: 'select', projectId: requestedId };
  const savedConversation = Boolean(query.get('session') || query.get('sessionId'));
  // Existing conversation links may carry the old flag; their history remains usable.
  const requestedSetup =
    query.get('setup') === 'true' ||
    (!savedConversation && query.get('onboarding_chat') === 'true');
  if (!project)
    return { mode: savedConversation && !requestedSetup ? 'home' : 'new', projectId: null };
  if (
    requestedSetup ||
    explicitNew ||
    (!savedConversation &&
      project.current_user_role !== 'editor' &&
      !isProjectWorkspaceReady(project))
  ) {
    return { mode: 'setup', projectId: project.id };
  }
  return { mode: 'home', projectId: project.id };
}

export function workspaceSetupHref(projectId: string, moduleId?: string | null): string {
  const query = new URLSearchParams({ setup: 'true', project_id: projectId });
  if (moduleId) query.set('setup_module', moduleId);
  return `/app/workspace?${query.toString()}`;
}
